import {
  ParamConfig,
  PARAMETER_DEFAULT_VALUES,
  SCRAPER_REQUEST_PARAMS,
  SCRAPING_PRODUCT,
  scrapingCSVFormat,
  targetConfig,
  targetParamMap,
} from '@smartproxy/sp-utils';

import {
  requestCodeByLanguageMap,
  RequestLanguage,
} from './constants/languages';
import { Target } from './constants/targets';

export type ParameterValue =
  | string
  | number
  | boolean
  | number[]
  | string[]
  | null
  | undefined;

type ScraperRequestParamType = ParamConfig['key'];

const NO_SELECTION = '';

type ConfigTypes = {
  [key in ScraperRequestParamType]: {
    targetKey: string;
    condition?: (value: ParameterValue) => boolean;
    format?: (
      value: ParameterValue,
      language?: RequestLanguage
    ) => ParameterValue;
  };
};

interface BuildRequestDataParams {
  target: Target;
  params: ParamConfig[];
  values: Record<ParamConfig['key'], ParameterValue>;
  language: RequestLanguage;
}

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

const config: ConfigTypes = {
  [SCRAPER_REQUEST_PARAMS.URL]: {
    targetKey: SCRAPER_REQUEST_PARAMS.URL,
    format: (value) => (value ? `"${value}"` : `""`),
  },
  [SCRAPER_REQUEST_PARAMS.QUERY]: {
    targetKey: SCRAPER_REQUEST_PARAMS.QUERY,
    format: (value) => (value ? `"${value}"` : `""`),
  },
  [SCRAPER_REQUEST_PARAMS.PARSE]: {
    targetKey: SCRAPER_REQUEST_PARAMS.PARSE,
    format: (value, language) =>
      language === 'Python' ? capitalize(String(value)) : value,
  },
  [SCRAPER_REQUEST_PARAMS.DOMAIN]: {
    targetKey: SCRAPER_REQUEST_PARAMS.DOMAIN,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAMS.LOCALE]: {
    targetKey: SCRAPER_REQUEST_PARAMS.LOCALE,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAMS.GEO]: {
    targetKey: SCRAPER_REQUEST_PARAMS.GEO,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAMS.GEO_CODE]: {
    targetKey: SCRAPER_REQUEST_PARAMS.GEO,
  },
  [SCRAPER_REQUEST_PARAMS.GEO_CODE_SELECT]: {
    targetKey: SCRAPER_REQUEST_PARAMS.GEO,
  },
  [SCRAPER_REQUEST_PARAMS.DEVICE_TYPE]: {
    targetKey: SCRAPER_REQUEST_PARAMS.DEVICE_TYPE,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAMS.PARSER_TYPE]: {
    targetKey: SCRAPER_REQUEST_PARAMS.PARSER_TYPE,
    condition: (value) => value !== NO_SELECTION,
  },
};

export const buildRequestData = ({
  target,
  params,
  values,
  language,
}: BuildRequestDataParams) => {
  let code = '';

  if (target) {
    code += `\n      "target": "${target.name}"`;
  }

  for (const param of params) {
    const parameterConfig = config[param.key];

    if (parameterConfig) {
      const { targetKey, condition, format } = parameterConfig;
      const paramValue = values[param.key];

      if (paramValue === undefined) {
        continue;
      }

      if (!condition || condition(paramValue)) {
        const formattedValue = format
          ? format(paramValue, language)
          : JSON.stringify(paramValue);

        code += `,\n      "${targetKey}": ${formattedValue}`;
      }
    }
  }

  return code;
};

const endpoint = 'https://scrape.decodo.com/v1/tasks';
const authHeader = 'Basic xxxxxxxxxxxxxxxx';
type GetRequestCodeParams = {
  target: Target;
  language: RequestLanguage;
  formValues: Record<string, string>;
};
export function buildRequestCode({
  target,
  language,
  formValues,
}: GetRequestCodeParams) {
  const buildCodeFn = requestCodeByLanguageMap[language];
  const params = targetParamMap[target.name].filter(
    (param) =>
      param.group === 'primary' ||
      param.group === 'regular' ||
      param.key === 'parse'
  );

  const code = buildCodeFn({
    url: endpoint,
    authorizationKey: authHeader,
    data: buildRequestData({
      target,
      params,
      values: formValues,
      language,
    }),
  });

  return code;
}

export const getPrettyProductLabel = (target: Target) => {
  for (const key in SCRAPING_PRODUCT) {
    const productName = SCRAPING_PRODUCT[key as keyof typeof SCRAPING_PRODUCT];
    if (productName === target.product) {
      return key;
    }
  }
};

const paramDefaults: Record<string, string> = {
  query: PARAMETER_DEFAULT_VALUES.DEFAULT_QUERY[0],
  device_type: PARAMETER_DEFAULT_VALUES.DEFAULT_DEVICE_DESKTOP,
  domain: 'com',
  locale: 'en-us',
  geo: 'United States',
};

export function convertParameterValueToString(val: ParameterValue) {
  if (val === undefined || val === null) return '';
  if (Array.isArray(val)) return String(val[0]);
  return String(val);
}

export function initFormValuesForTarget(target: Target) {
  const params = targetParamMap[target.name];
  const config = targetConfig[target.name];
  const values: Record<string, string | boolean> = {};

  for (const param of params) {
    values[param.key] = convertParameterValueToString(
      config.defaults[param.key] ?? paramDefaults[param.key]
    );
  }
  if (config.format === 'json') {
    values['parse'] = true;
  }

  return values;
}

export function initFormValidatorForTarget(target: Target) {
  const params = targetParamMap[target.name];
  const validators: Record<string, (value: string) => string> = {};

  for (const param of params) {
    if (param.group !== 'primary') continue;
    if (param.key === 'url') {
      validators[param.key] = (value: string) => {
        if (!/^https?:\/\//.test(value))
          return 'URL must contain http:// or https://';
        if (URL.canParse(value)) return '';
        return 'Enter a valid URL';
      };
    } else {
      validators[param.key] = (value: string) =>
        value === '' ? 'Required' : '';
    }
  }

  return validators;
}

export const getTabularData = ({
  target,
  json,
}: {
  target: Target;
  json: Record<string, unknown>;
}) => {
  const config = scrapingCSVFormat.get(target.name);

  const jsonTruthy = Object.keys(json).length > 0;

  return {
    columns: config?.columns || [],
    data: jsonTruthy && config?.selectData ? config?.selectData(json) : [{}],
    rowAccessor: config?.rowAccessor || undefined,
  };
};
